import React from 'react';

export const AnalyticsChart = ({ title, type = 'bar', data = null }) => {
  // Sample data for demonstration
  const defaultBarData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      { label: 'Income', values: [5000, 6000, 5500, 7000, 6500, 8000] },
      { label: 'Expenses', values: [2000, 2500, 2200, 2800, 2600, 3000] },
    ],
  };

  let chartData;
  
  if (type === 'bar') {
    // For bar charts, validate that we have both datasets with values
    if (data && data.datasets && data.datasets.length >= 2 && 
        data.datasets[0]?.values && data.datasets[1]?.values &&
        data.labels && data.labels.length > 0) {
      chartData = data;
    } else {
      chartData = defaultBarData;
    }
  } else {
    // For pie charts, validate that we have labels and values
    if (data && data.labels && data.values && 
        data.labels.length > 0 && data.values.length > 0) {
      chartData = data;
    } else {
      chartData = null;
    }
  }

  if (type === 'bar') {
    // Check if all values are zero
    const allValuesZero = 
      chartData.datasets[0].values.every(v => v === 0) && 
      chartData.datasets[1].values.every(v => v === 0);

    if (allValuesZero) {
      return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h3>
          
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">No transaction data yet</p>
            <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">Add transactions to see your income vs expenses</p>
          </div>
        </div>
      );
    }

    const maxVal = Math.max(...chartData.datasets[0].values, ...chartData.datasets[1].values);
    
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h3>
        
        {/* Chart Summary */}
        <div className="flex gap-6 mb-6">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Income</p>
            <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">₦{chartData.datasets[0].values.reduce((a, b) => a + b, 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Expenses</p>
            <p className="text-lg font-semibold text-rose-600 dark:text-rose-400">₦{chartData.datasets[1].values.reduce((a, b) => a + b, 0).toLocaleString()}</p>
          </div>
        </div>
        
        {/* Combined Chart */}
        <div className="flex gap-2 sm:gap-4 items-end h-40 sm:h-64 bg-gray-50 dark:bg-gray-900 p-3 sm:p-4 rounded-lg overflow-x-auto">
          {chartData.datasets[0].values.map((incomeVal, idx) => {
            const expenseVal = chartData.datasets[1].values[idx];
            const incomeHeight = (incomeVal / maxVal) * 100;
            const expenseHeight = (expenseVal / maxVal) * 100;
            
            return (
              <div key={idx} className="flex-shrink-0 flex flex-col items-center gap-1 sm:gap-2 h-full" style={{ minWidth: '48px' }}>
                <div className="w-full h-full flex items-end justify-center gap-0.5">
                  {/* Income Bar */}
                  <div
                    className="bg-blue-500 hover:bg-blue-600 rounded-t transition-all cursor-pointer flex-1 relative group"
                    style={{ height: `${incomeHeight}%`, minHeight: '4px' }}
                    title={`Income: ₦${incomeVal.toLocaleString()}`}
                  >
                    <span className="absolute -top-6 sm:-top-7 left-1/2 transform -translate-x-1/2 text-xs font-bold text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-blue-100 px-2 py-1 rounded shadow z-10">
                      ₦{(incomeVal / 1000).toFixed(0)}K
                    </span>
                  </div>
                  
                  {/* Expense Bar */}
                  <div
                    className="bg-rose-500 hover:bg-rose-600 rounded-t transition-all cursor-pointer flex-1 relative group"
                    style={{ height: `${expenseHeight}%`, minHeight: '4px' }}
                    title={`Expense: ₦${expenseVal.toLocaleString()}`}
                  >
                    <span className="absolute -top-6 sm:-top-7 left-1/2 transform -translate-x-1/2 text-xs font-bold text-rose-700 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-rose-100 px-2 py-1 rounded shadow z-10">
                      ₦{(expenseVal / 1000).toFixed(0)}K
                    </span>
                  </div>
                </div>
                
                <span className="text-xs text-gray-700 dark:text-gray-300 font-semibold mt-1 text-center">{chartData.labels[idx]}</span>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex gap-6 mt-6 justify-center flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Income</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-rose-500 rounded"></div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Expenses</span>
          </div>
        </div>
      </div>
    );
  }

  // Pie Chart
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h3>
      
      {!chartData ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="text-gray-400 dark:text-gray-500 mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          </div>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">No category data yet</p>
          <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">Add transactions to see category breakdown</p>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-8">
          {/* Simple Pie Chart using SVG */}
          <svg width="280" height="280" className="flex-shrink-0 mt-6">
            {(() => {
              const total = chartData.values.reduce((a, b) => a + b, 0);
              const colors = ['#2a4dd0', '#ef4444', '#eab308', '#10b981', '#f97316'];
              let currentAngle = -90;

              return chartData.values.map((val, idx) => {
                const sliceAngle = (val / total) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + sliceAngle;
                const x1 = 140 + 125 * Math.cos((startAngle * Math.PI) / 180);
                const y1 = 140 + 125 * Math.sin((startAngle * Math.PI) / 180);
                const x2 = 140 + 125 * Math.cos((endAngle * Math.PI) / 180);
                const y2 = 140 + 125 * Math.sin((endAngle * Math.PI) / 180);
                const largeArc = sliceAngle > 180 ? 1 : 0;

                const pathData = [
                  `M 140 140`,
                  `L ${x1} ${y1}`,
                  `A 125 125 0 ${largeArc} 1 ${x2} ${y2}`,
                  'Z',
                ].join(' ');

                currentAngle = endAngle;

                return (
                  <path key={idx} d={pathData} fill={colors[idx % colors.length]} stroke="white" strokeWidth="2" />
                );
              });
            })()}
          </svg>

          {/* Legend */}
          <div className="flex flex-col gap-2 text-xs sm:text-sm">
            {chartData.labels.map((label, idx) => {
              const colors = ['#2a4dd0', '#ef4444', '#eab308', '#10b981', '#f97316'];
              const total = chartData.values.reduce((a, b) => a + b, 0);
              const percentage = ((chartData.values[idx] / total) * 100).toFixed(1);
              return (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: colors[idx % colors.length] }}
                  ></div>
                  <span className="text-gray-600 dark:text-gray-400">
                    {label}: {percentage}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
